import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { BuildingType } from "./types";
import "./LayoutEditor.css";

/**
 * Beta-only layout editor for the village map.
 *
 * Lets a tester nudge each building's position, scale, name label and
 * decoration offsets live on the map, then copy the result as JSON to send
 * back so the values can be written into mapData.ts. Edits only live in this
 * browser (localStorage) — nothing is sent anywhere and nothing changes for
 * other players.
 */

const STORAGE_KEY = "village.layoutEditor";
const NUDGE_STEPS = [1, 5, 25];
const SCALE_STEP = 0.01;

interface DecorationLayout {
  offsetX: number;
  offsetY: number;
  scale: number;
}

interface BuildingLayout {
  x: number;
  y: number;
  scale: number;
  namePosition?: { x: number; y: number };
  decorations?: DecorationLayout[];
}

type LayoutOverrides = Record<number, BuildingLayout>;

const round = (value: number) => Math.round(value * 1000) / 1000;

const layoutOf = (building: BuildingType): BuildingLayout => ({
  x: building.x,
  y: building.y,
  scale: building.scale,
  namePosition: building.namePosition
    ? { ...building.namePosition }
    : undefined,
  decorations: building.decorations?.length
    ? building.decorations.map((decoration) => ({
        offsetX: decoration.offsetX,
        offsetY: decoration.offsetY,
        scale: decoration.scale ?? 1,
      }))
    : undefined,
});

const applyLayout = (
  building: BuildingType,
  layout: BuildingLayout
): BuildingType => ({
  ...building,
  x: layout.x,
  y: layout.y,
  scale: layout.scale,
  namePosition: layout.namePosition ?? building.namePosition,
  decorations: building.decorations?.map((decoration, index) => ({
    ...decoration,
    ...layout.decorations?.[index],
  })),
});

const sameLayout = (a: BuildingLayout, b: BuildingLayout) =>
  JSON.stringify(a) === JSON.stringify(b);

const loadOverrides = (): LayoutOverrides => {
  try {
    const saved = JSON.parse(localStorage.getItem(STORAGE_KEY) || "{}");
    return saved && typeof saved === "object" ? saved : {};
  } catch {
    return {};
  }
};

const saveOverrides = (overrides: LayoutOverrides) => {
  try {
    if (Object.keys(overrides).length) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(overrides));
    } else {
      localStorage.removeItem(STORAGE_KEY);
    }
  } catch {}
};

const copyText = async (text: string) => {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {}

  // Older / non-secure contexts: fall back to a hidden textarea.
  const textarea = document.createElement("textarea");
  textarea.value = text;
  textarea.style.position = "fixed";
  textarea.style.opacity = "0";
  document.body.appendChild(textarea);
  textarea.select();
  let copied = false;
  try {
    copied = document.execCommand("copy");
  } catch {}
  textarea.remove();
  return copied;
};

/**
 * Holds the edited layout. When `enabled` is false it is inert: the base
 * buildings are returned untouched and localStorage is never read.
 */
export const useVillageLayoutEditor = (
  baseBuildings: BuildingType[],
  enabled: boolean
) => {
  const [overrides, setOverrides] = useState<LayoutOverrides>(() =>
    enabled ? loadOverrides() : {}
  );
  const [open, setOpen] = useState(false);
  const [selectedId, setSelectedId] = useState<number>(baseBuildings[0]?.id);

  useEffect(() => {
    if (enabled) saveOverrides(overrides);
  }, [enabled, overrides]);

  // Unedited buildings keep their original object, so memoised sprites for
  // them don't re-render while another building is being moved.
  const buildings = useMemo(
    () =>
      baseBuildings.map((building) =>
        overrides[building.id]
          ? applyLayout(building, overrides[building.id])
          : building
      ),
    [baseBuildings, overrides]
  );

  const updateLayout = (
    id: number,
    change: (layout: BuildingLayout) => BuildingLayout
  ) => {
    const base = baseBuildings.find((building) => building.id === id);
    if (!base) return;
    setOverrides((current) => {
      const original = layoutOf(base);
      const next = change(current[id] ?? original);
      const { [id]: _, ...rest } = current;
      return sameLayout(next, original) ? rest : { ...rest, [id]: next };
    });
  };

  const resetLayout = (id?: number) =>
    setOverrides((current) => {
      if (id === undefined) return {};
      const { [id]: _, ...rest } = current;
      return rest;
    });

  const changedBuildings = baseBuildings.filter(
    (building) => overrides[building.id]
  );

  const exportText = JSON.stringify(
    {
      villageLayout: 1,
      buildings: changedBuildings.map((building) => ({
        id: building.id,
        name: building.name,
        ...overrides[building.id],
      })),
    },
    null,
    2
  );

  return {
    buildings,
    // Only drive the map highlight while the panel is actually open.
    selectedId: enabled && open ? selectedId : null,
    open,
    setOpen,
    setSelectedId,
    baseBuildings,
    changedCount: changedBuildings.length,
    isChanged: (id: number) => !!overrides[id],
    updateLayout,
    resetLayout,
    exportText,
  };
};

type LayoutEditorState = ReturnType<typeof useVillageLayoutEditor>;

interface NumberFieldProps {
  label: string;
  value: number;
  step: number;
  onChange: (value: number) => void;
}

const NumberField: React.FC<NumberFieldProps> = ({
  label,
  value,
  step,
  onChange,
}) => {
  // Keep the typed text separately so partial input like "-" or "0." isn't
  // overwritten by the parsed number mid-typing.
  const [draft, setDraft] = useState(String(value));

  useEffect(() => {
    if (parseFloat(draft) !== value) setDraft(String(value));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  return (
    <div className="vle-field">
      <span className="vle-field-label">{label}</span>
      <button
        type="button"
        className="vle-nudge"
        onClick={() => onChange(round(value - step))}
        aria-label={`Decrease ${label}`}
      >
        −
      </button>
      <input
        type="number"
        className="vle-input"
        step={step}
        value={draft}
        onChange={(event) => {
          setDraft(event.target.value);
          const parsed = parseFloat(event.target.value);
          if (Number.isFinite(parsed)) onChange(parsed);
        }}
        onBlur={() => setDraft(String(value))}
      />
      <button
        type="button"
        className="vle-nudge"
        onClick={() => onChange(round(value + step))}
        aria-label={`Increase ${label}`}
      >
        +
      </button>
    </div>
  );
};

interface BuildingPickerProps {
  buildings: BuildingType[];
  selectedId: number | null;
  isChanged: (id: number) => boolean;
  onSelect: (id: number) => void;
}

/**
 * Building dropdown drawn in the DOM. A native <select> opens a browser-drawn
 * popup the page can't see, so the map under it kept reacting to the pointer
 * (hover sound, tint) while the options were open.
 */
const BuildingPicker: React.FC<BuildingPickerProps> = ({
  buildings,
  selectedId,
  isChanged,
  onSelect,
}) => {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const closeOutside = (event: PointerEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("pointerdown", closeOutside);
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("pointerdown", closeOutside);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [open]);

  const selected = buildings.find((b) => b.id === selectedId);

  return (
    <div className="vle-picker" ref={rootRef}>
      <button
        type="button"
        className="vle-picker-button"
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((current) => !current)}
      >
        <span>
          {selected && isChanged(selected.id) && (
            <span className="vle-dot" aria-hidden>
              ●
            </span>
          )}
          {selected ? `${selected.name} (#${selected.id})` : "Pick a building"}
        </span>
        <span className="vle-caret" aria-hidden>
          {open ? "▴" : "▾"}
        </span>
      </button>
      {open && (
        <div className="vle-picker-list" role="listbox">
          {buildings.map((b) => (
            <button
              key={b.id}
              type="button"
              role="option"
              aria-selected={b.id === selectedId}
              className={`vle-picker-option${
                b.id === selectedId ? " is-selected" : ""
              }`}
              onClick={() => {
                onSelect(b.id);
                setOpen(false);
              }}
            >
              {isChanged(b.id) && (
                <span className="vle-dot" aria-hidden>
                  ●
                </span>
              )}
              {b.name} (#{b.id})
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export const VillageLayoutEditor: React.FC<{ editor: LayoutEditorState }> = ({
  editor,
}) => {
  const [nudge, setNudge] = useState(NUDGE_STEPS[0]);
  const [status, setStatus] = useState<string | null>(null);
  const [showExport, setShowExport] = useState(false);

  const {
    open,
    setOpen,
    selectedId,
    setSelectedId,
    buildings,
    baseBuildings,
    changedCount,
    isChanged,
    updateLayout,
    resetLayout,
    exportText,
  } = editor;

  const building = buildings.find((b) => b.id === selectedId);
  const layout = building ? layoutOf(building) : null;

  const update = (change: (layout: BuildingLayout) => BuildingLayout) => {
    if (building) updateLayout(building.id, change);
  };

  const handleCopy = async () => {
    if (!changedCount) {
      setStatus("Nothing changed yet.");
      return;
    }
    const copied = await copyText(exportText);
    setShowExport(!copied);
    setStatus(
      copied
        ? `Copied ${changedCount} building${changedCount > 1 ? "s" : ""}.`
        : "Couldn't copy — select the text below and copy it."
    );
  };

  const handleResetAll = () => {
    if (window.confirm("Reset every building to its original layout?")) {
      resetLayout();
      setShowExport(false);
      setStatus(null);
    }
  };

  if (!open) {
    return createPortal(
      <button
        type="button"
        className="vle-toggle"
        onClick={() => setOpen(true)}
      >
        Layout
        {changedCount > 0 && <span className="vle-badge">{changedCount}</span>}
      </button>,
      document.body
    );
  }

  return createPortal(
    <div className="vle-panel" role="dialog" aria-label="Village layout editor">
      <div className="vle-header">
        <strong>Village layout</strong>
        <span className="vle-tag">beta</span>
        <button
          type="button"
          className="vle-close"
          onClick={() => setOpen(false)}
          aria-label="Close layout editor"
        >
          ×
        </button>
      </div>

      <BuildingPicker
        buildings={baseBuildings}
        selectedId={selectedId}
        isChanged={isChanged}
        onSelect={setSelectedId}
      />

      <div className="vle-steps">
        <span className="vle-field-label">Step</span>
        {NUDGE_STEPS.map((step) => (
          <button
            key={step}
            type="button"
            className={`vle-step${nudge === step ? " is-active" : ""}`}
            onClick={() => setNudge(step)}
          >
            {step}px
          </button>
        ))}
      </div>

      {building && layout && (
        <>
          <div className="vle-group">Building</div>
          <NumberField
            label="X"
            value={layout.x}
            step={nudge}
            onChange={(x) => update((l) => ({ ...l, x }))}
          />
          <NumberField
            label="Y"
            value={layout.y}
            step={nudge}
            onChange={(y) => update((l) => ({ ...l, y }))}
          />
          <NumberField
            label="Scale"
            value={layout.scale}
            step={SCALE_STEP}
            onChange={(scale) => update((l) => ({ ...l, scale }))}
          />

          {layout.namePosition && (
            <>
              <div className="vle-group">Name label offset</div>
              <NumberField
                label="X"
                value={layout.namePosition.x}
                step={nudge}
                onChange={(x) =>
                  update((l) => ({
                    ...l,
                    namePosition: { ...l.namePosition!, x },
                  }))
                }
              />
              <NumberField
                label="Y"
                value={layout.namePosition.y}
                step={nudge}
                onChange={(y) =>
                  update((l) => ({
                    ...l,
                    namePosition: { ...l.namePosition!, y },
                  }))
                }
              />
            </>
          )}

          {layout.decorations?.map((decoration, index) => {
            const updateDecoration = (patch: Partial<DecorationLayout>) =>
              update((l) => ({
                ...l,
                decorations: l.decorations?.map((d, i) =>
                  i === index ? { ...d, ...patch } : d
                ),
              }));

            return (
              <React.Fragment key={index}>
                <div className="vle-group">Decoration {index + 1} offset</div>
                <NumberField
                  label="X"
                  value={decoration.offsetX}
                  step={nudge}
                  onChange={(offsetX) => updateDecoration({ offsetX })}
                />
                <NumberField
                  label="Y"
                  value={decoration.offsetY}
                  step={nudge}
                  onChange={(offsetY) => updateDecoration({ offsetY })}
                />
                <NumberField
                  label="Scale"
                  value={decoration.scale}
                  step={SCALE_STEP}
                  onChange={(scale) => updateDecoration({ scale })}
                />
              </React.Fragment>
            );
          })}

          <button
            type="button"
            className="vle-link"
            disabled={!isChanged(building.id)}
            onClick={() => resetLayout(building.id)}
          >
            Reset this building
          </button>
        </>
      )}

      <div className="vle-footer">
        <button type="button" className="vle-primary" onClick={handleCopy}>
          Copy export{changedCount > 0 ? ` (${changedCount})` : ""}
        </button>
        <button
          type="button"
          className="vle-secondary"
          disabled={!changedCount}
          onClick={handleResetAll}
        >
          Reset all
        </button>
      </div>
      {status && <div className="vle-status">{status}</div>}
      {showExport && (
        <textarea
          className="vle-export"
          readOnly
          value={exportText}
          onFocus={(event) => event.target.select()}
        />
      )}
      <div className="vle-hint">
        Changes are saved in this browser only. Send the copied text to the
        developer to make them permanent.
      </div>
    </div>,
    document.body
  );
};
